<div class="alerts">
  <app-page-header breadcrumb="Administration" [doubleArrows]="true" title="Alerts" [keepIconOnMobile]="true">
    <!-- The live count + "End-user preview" button are desktop-only: on mobile, Preview is its
         own tab (below), so both would be redundant AND overflow/clip the narrow header. -->
    @if (!(isMobile$ | async)) {
      @if (liveAlertCount$ | async; as count) {
        <span class="alerts__live-count">
          <mat-icon class="material-symbols-outlined">notifications_active</mat-icon>
          {{ count }} live alert{{ count === 1 ? '' : 's' }}
        </span>
      }
      <button class="alerts__preview-button" mat-stroked-button (click)="openPreview()">
        <mat-icon class="material-symbols-outlined">visibility</mat-icon>
        End-user preview
        <span class="sgl-arrows" aria-hidden="true"></span>
      </button>
    }
  </app-page-header>

  @if (isMobile$ | async) {
    <!-- Mobile: a single NEW / ALERTS / PREVIEW tab strip replaces desktop's two-cards-always-
         visible layout + separate preview dialog — see the plan's context for why.
         mat-stretch-tabs="true" forces 3 equal-width tabs that always fit the viewport, instead
         of Material's default "shrink to content, paginate with arrows" behavior once labels
         don't all fit — which is what caused the mobile build's "< NEW >" carousel-arrow bug. -->
    <mat-tab-group
      #mobileTabs
      class="alerts__tabs"
      mat-stretch-tabs="true"
      [disablePagination]="true"
      disableRipple
      animationDuration="0ms"
      [(selectedIndex)]="selectedMobileTabIndex"
      (selectedIndexChange)="onMobileTabChange($event)">
      <mat-tab>
        <ng-template mat-tab-label>New</ng-template>
        <div class="alerts__card alerts__mobile-card">
          <app-alert-form [target]="formTarget" (saved)="onAlertSaved()" (cleared)="onFormCleared()"></app-alert-form>
        </div>
      </mat-tab>

      <mat-tab>
        <ng-template mat-tab-label>List ({{ tableData.totalCount }})</ng-template>
        <div class="alerts__card alerts__mobile-card">
          <div class="alerts__mobile-search">
            <mat-icon class="material-symbols-outlined">search</mat-icon>
            <input
              type="text"
              placeholder="Filter by message"
              [formControl]="mobileSearchControl"
              (keydown.enter)="onMobileSearch()" />
            @if (mobileSearchControl.value) {
              <mat-icon class="material-symbols-outlined alerts__mobile-search-clear" (click)="clearMobileSearch()">
                close
              </mat-icon>
            }
          </div>
          <div class="alerts__mobile-pills">
            <button
              type="button"
              class="alerts__mobile-pill"
              [class.alerts__mobile-pill--selected]="selectedMobileStatus === ''"
              (click)="onMobileStatusFilter('')">
              All
            </button>
            @for (status of statuses; track status) {
              <button
                type="button"
                class="alerts__mobile-pill"
                [class.alerts__mobile-pill--selected]="selectedMobileStatus === status"
                (click)="onMobileStatusFilter(status)">
                {{ statusMeta(status).label }}
              </button>
            }
          </div>

          <div class="alerts__list-heading">
            <p class="alerts__list-breadcrumb">Configured alerts<span class="sgl-arrows" aria-hidden="true"></span></p>
            <p class="alerts__list-description">
              {{ tableData.totalCount }} total · {{ (liveAlertCount$ | async) ?? 0 }} live
            </p>
          </div>

          @if (isTableInitiating) {
            <div class="spinner">
              <mat-spinner diameter="60"></mat-spinner>
            </div>
          } @else {
            <div class="alerts__mobile-cards">
              @for (alert of tableData.data; track alert.id) {
                <app-alert-card
                  [alert]="alert"
                  (edit)="editAlert(alert)"
                  (duplicate)="duplicateAlert(alert)"
                  (delete)="deleteAlert(alert)"
                  (toggleActive)="toggleActive(alert, $event)"></app-alert-card>
              } @empty {
                <p class="alerts__mobile-empty">No alerts found</p>
              }
            </div>
          }
          @if (isLoading && !isTableInitiating) {
            <div class="spinner">
              <mat-spinner diameter="60"></mat-spinner>
            </div>
          }
        </div>
      </mat-tab>

      <mat-tab>
        <ng-template mat-tab-label>Preview</ng-template>
        <div class="alerts__card alerts__mobile-card">
          <p class="alerts__preview-mobile-desc">
            {{ previewVisibleAlerts.length }} live alert{{ previewVisibleAlerts.length === 1 ? '' : 's' }} — what a user
            sees right now, over whatever they're doing.
          </p>
          <div class="alerts__preview-mobile-bar">
            <span class="sgl-arrows sgl-arrows--lg" aria-hidden="true"></span>
            OVERVIEW
          </div>
          <div class="alerts__preview-mobile-body">
            <app-alert-preview-stack
              [loading]="previewLoading"
              [hasError]="previewHasError"
              [alerts]="previewVisibleAlerts"
              (dismiss)="dismissPreview($event)"></app-alert-preview-stack>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <div class="alerts__body" #alertsBody>
      <div class="alerts__card">
        <app-alert-form [target]="formTarget" (saved)="onAlertSaved()" (cleared)="onFormCleared()"></app-alert-form>
      </div>

      <div class="alerts__card">
        <div class="alerts__list-heading">
          <p class="alerts__list-breadcrumb">Configured alerts<span class="sgl-arrows" aria-hidden="true"></span></p>
          <p class="alerts__list-description">
            Live alerts show to every mySGL user as a stacked notification that stays until they close it — a closed
            alert returns at the user's next login while it remains live.
          </p>
        </div>

        @if (isTableInitiating) {
          <div class="spinner">
            <mat-spinner diameter="60"></mat-spinner>
          </div>
        } @else {
          <div class="alerts__action-buttons">
            <app-alert-filters (filterApplied)="onFilterChange($event)"></app-alert-filters>
            <span class="alerts__count">{{ tableData.totalCount }} alerts</span>
          </div>
          <div class="alerts__table-wrapper">
            <cdk-virtual-scroll-viewport [itemSize]="rowHeight" class="alerts__table-viewport">
              <table
                mat-table
                recycleRows
                [dataSource]="dataSource"
                class="alerts__table"
                matSort
                matSortActive="status"
                matSortDirection="asc">
                <ng-container matColumnDef="level">
                  <th mat-header-cell mat-sort-header *matHeaderCellDef>Level</th>
                  <td mat-cell *matCellDef="let element">
                    <span
                      class="alerts__badge"
                      [style.color]="levelMeta(element.level).color"
                      [ngClass]="'sgl-alert-' + levelMeta(element.level).label">
                      <mat-icon class="material-symbols-outlined">{{ levelMeta(element.level).icon }}</mat-icon>
                      {{ levelMeta(element.level).label }}
                    </span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="message">
                  <th mat-header-cell *matHeaderCellDef>Message</th>
                  <td mat-cell *matCellDef="let element">
                    <span class="alerts__message">{{ element.message }}</span>
                  </td>
                </ng-container>
                <!-- Sort ids are kept camelCase (matching this app's convention — see users.component.html's
             'name'/'email' etc.) even though the backend's SearchAlertsSortField enum members are
             PascalCase ("ShowFrom"/"LastUpdatedAt") — same case-insensitive match Users' sort
             already relies on. -->
                <ng-container matColumnDef="schedule">
                  <th mat-header-cell mat-sort-header="showFrom" *matHeaderCellDef>Schedule</th>
                  <td mat-cell *matCellDef="let element">
                    @if (element.visibilityMode === 'Manual') {
                      Manual — on until switched off
                    } @else {
                      {{ element.showFrom | date: 'd MMM y, HH:mm' }} –
                      {{ element.showUntil | date: 'd MMM y, HH:mm' }}
                    }
                  </td>
                </ng-container>
                <ng-container matColumnDef="status">
                  <th mat-header-cell mat-sort-header *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let element">
                    <span class="alerts__badge" [style.color]="statusMeta(element.status).color">
                      <span class="alerts__status-dot"></span>
                      {{ statusMeta(element.status).label }}
                    </span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="lastChanged">
                  <th mat-header-cell mat-sort-header="lastUpdatedAt" *matHeaderCellDef>Last changed</th>
                  <!-- id: see the comment on the "schedule" column above. -->
                  <td mat-cell *matCellDef="let element">
                    <div class="alerts__last-changed">
                      <span>{{ element.lastUpdatedAt | date: 'd MMMM y, HH:mm' }}</span>
                      <span class="alerts__last-changed-by">{{ element.lastUpdatedBy }}</span>
                    </div>
                  </td>
                </ng-container>
                <ng-container matColumnDef="active">
                  <th mat-header-cell *matHeaderCellDef>Active</th>
                  <td mat-cell *matCellDef="let element">
                    <label class="alert-toggle">
                      <input
                        type="checkbox"
                        class="alert-toggle__input"
                        [checked]="element.isActive"
                        (change)="toggleActive(element, $event)"
                        [attr.aria-label]="'Toggle active: ' + element.message" />
                      <span class="alert-toggle__track"><span class="alert-toggle__thumb"></span></span>
                    </label>
                  </td>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef>Actions</th>
                  <td mat-cell *matCellDef="let element">
                    <button matIconButton (click)="editAlert(element)" matTooltip="Edit alert">
                      <mat-icon class="material-symbols-outlined">edit</mat-icon>
                    </button>
                    <button matIconButton (click)="duplicateAlert(element)" matTooltip="Duplicate alert">
                      <mat-icon class="material-symbols-outlined">content_copy</mat-icon>
                    </button>
                    <button matIconButton (click)="deleteAlert(element)" matTooltip="Delete alert">
                      <mat-icon class="material-symbols-outlined">delete</mat-icon>
                    </button>
                  </td>
                </ng-container>
                <!-- Not sticky: a cdk-virtual-scroll-viewport scrolls its content via `transform:
                     translateY(...)`, and a transform on an ancestor creates a new containing
                     block for any position: sticky descendant — so a sticky header in here would
                     drift with that transform instead of staying pinned (see Contacts'
                     VIRTUAL_SCROLL.md "Gotcha #2", which fixed this for its own grid by moving
                     the header row entirely outside the viewport). This table's columns are few
                     and narrow enough that a non-sticky header is an acceptable trade-off rather
                     than taking on that same two-layer header/column-width restructuring here. -->
                <tr mat-header-row *matHeaderRowDef="displayedColumns" class="alerts__table--header-row"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
                <tr *matNoDataRow>
                  <td [attr.colspan]="displayedColumns.length">No alerts found</td>
                </tr>
              </table>
            </cdk-virtual-scroll-viewport>
          </div>
        }
        @if (isLoading && !isTableInitiating) {
          <div class="spinner">
            <mat-spinner diameter="60"></mat-spinner>
          </div>
        }
      </div>
    </div>
  }
</div>
